import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { Dialog, Modal } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { aiImagePromptQuery, generateAIImage } from '@/api/recipes'
import { Button } from '@/components/Button'
import { Callout } from '@/components/Callout/Callout'
import { Flex } from '@/components/Flex'
import { ModalContent, ModalFooter, ModalHeader } from '@/components/Modal'
import { Spinner } from '@/components/Spinner'
import { Textarea } from '@/components/Textarea'
import useRecipePhoto from './useRecipePhoto'

type Props = {
  isOpen: boolean
  idRecipe: number
  setOpen: (state: boolean) => void
}

export default function GenerateImageModal({ isOpen, idRecipe, setOpen }: Props) {
  const { t } = useTranslation()
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const { uploadPhoto } = useRecipePhoto(idRecipe)

  const { data: defaultPrompt, isLoading } = useQuery({
    ...aiImagePromptQuery(idRecipe),
    enabled: isOpen,
  })

  const generateMutation = useMutation({
    mutationFn: async (instructions: string) => {
      const blob = await generateAIImage(idRecipe, instructions)
      const file = new File([blob], 'ai-generated-image.png', { type: 'image/png' })

      await uploadPhoto(file, { photoSource: 'AI_GENERATED', isCover: false })
    },
    onSuccess: () => {
      toast.success('AI image generated and uploaded successfully!')
      setAdditionalInstructions('')
      setOpen(false)
    },
    onError: (error) => {
      toast.error('Failed to generate and upload AI image')
    },
  })

  const handleGenerate = () => {
    generateMutation.reset()
    generateMutation.mutate(additionalInstructions)
  }

  const handleClose = (open: boolean) => {
    if (!open) {
      generateMutation.reset()
      setAdditionalInstructions('')
    }
    setOpen(open)
  }

  return (
    <Modal
      style={{ width: 500, minHeight: 'auto' }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={handleClose}>
      <Dialog aria-label="Generate AI Image">
        {({ close }) => (
          <>
            <ModalHeader onClose={close}>{t('photos.generateAIImage')}</ModalHeader>

            <ModalContent
              style={{
                position: 'relative',
                overflow: 'visible',
              }}>
              {isLoading ? (
                <Spinner />
              ) : (
                <Flex direction="column">
                  {generateMutation.isError && (
                    <Callout variant="danger">
                      {generateMutation.error instanceof Error && (
                        <div>{generateMutation.error.message}</div>
                      )}
                    </Callout>
                  )}

                  <Textarea
                    label={t('photos.genDefaultPrompt')}
                    value={defaultPrompt || ''}
                    isDisabled
                  />

                  <Textarea
                    label={t('photos.genAdditionalInstructions')}
                    value={additionalInstructions}
                    onChange={setAdditionalInstructions}
                  />

                  {generateMutation.isPending && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '0',
                        top: '0',
                        right: '0',
                        bottom: '0',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                      }}>
                      <Spinner />
                      {t('photos.genProcessing')}
                    </div>
                  )}
                </Flex>
              )}
            </ModalContent>

            <ModalFooter onClose={close}>
              <Button
                variant="primary"
                onPress={handleGenerate}
                isDisabled={generateMutation.isPending || isLoading}>
                {t('photos.generateImage')}
              </Button>
            </ModalFooter>
          </>
        )}
      </Dialog>
    </Modal>
  )
}
